// A "By company" drop-down you can type into (module 10 U228; pipeline F1, B1950). Hundreds of companies do not fit a
// plain list, so the chip opens a box: typing cuts the options down, the arrow keys and Enter choose, Esc closes. The
// options and their counts are the API's, for the whole list as the reader sees it (U152), never the rows on screen.
// F5 builds CompanyPicker on this, reading the same company search as the top bar's box.
import { ActionButton, ActionInput } from '../parts/ActionButton.js';
import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import './find.css';

export interface PickerOption { id: string; name: string; n?: number }

/** At most this many options are drawn at once; the rest are reached by typing. */
export const PICKER_SHOWN = 100;

/** Lower case with the accents taken off, so "kambi" finds "Kambi" and "lopez" finds "López". */
export const fold = (s: string): string => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

/** The options whose name holds every word typed, those that start with the first word first; at most `limit`. */
export function narrow(options: readonly PickerOption[], typed: string, limit = PICKER_SHOWN): { shown: PickerOption[]; more: number } {
  const words = fold(typed).split(/\s+/).filter(Boolean);
  const hits = words.length === 0 ? [...options] : options.filter((o) => { const n = fold(o.name); return words.every((w) => n.includes(w)); });
  if (words.length > 0) {
    const first = words[0]!;
    hits.sort((a, b) => Number(fold(b.name).startsWith(first)) - Number(fold(a.name).startsWith(first)));
  }
  return { shown: hits.slice(0, limit), more: Math.max(0, hits.length - limit) };
}

/** Where the open box sits: under the chip, inside the window. Fixed, because the chip row scrolls sideways on a phone and would clip it. */
function place(chip: HTMLElement): { top: number; left: number; width: number } {
  const r = chip.getBoundingClientRect();
  const width = Math.min(320, window.innerWidth - 16);
  return { top: r.bottom + 4, left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)), width };
}

export function Picker({ label, find, any, value, options, onChange }: {
  /** The chip's words when nothing is chosen: "By company". */
  label: string;
  /** The box's own label and placeholder: "Find a company". */
  find: string;
  /** The first option, which takes the narrowing off: "Any company". */
  any: string;
  /** The chosen option's id, or ''. */
  value: string;
  /** null while the options are still being read. */
  options: readonly PickerOption[] | null;
  onChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [at, setAt] = useState(0);
  const [box, setBox] = useState<{ top: number; left: number; width: number } | null>(null);
  const chip = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const id = useId();

  const chosen = value ? options?.find((o) => o.id === value) ?? null : null;
  const { shown, more } = narrow(options ?? [], typed);
  // Row 0 is "Any company"; the options follow it.
  const rows: PickerOption[] = [{ id: '', name: any }, ...shown];

  const close = (refocus: boolean) => { setOpen(false); setTyped(''); if (refocus) chip.current?.focus(); };
  const choose = (o: PickerOption) => { close(true); if (o.id !== value) onChange(o.id); };

  useLayoutEffect(() => {
    if (!open || !chip.current) return undefined;
    const follow = () => { if (chip.current) setBox(place(chip.current)); };
    follow();
    window.addEventListener('resize', follow);
    window.addEventListener('scroll', follow, true);
    return () => { window.removeEventListener('resize', follow); window.removeEventListener('scroll', follow, true); };
  }, [open]);

  // The box takes the cursor once it is placed. Before then it is drawn hidden, and a browser will not focus a hidden
  // input: focusing it in the effect above left the cursor where it was, so what was typed next went to the page's
  // find field instead of the box (found on Tasks, 2026-10-04, pipeline F5; jsdom focuses a hidden input, so the
  // render test never saw it).
  const placed = box !== null;
  useEffect(() => { if (open && placed) input.current?.focus(); }, [open, placed]);

  useEffect(() => {
    if (!open) return undefined;
    const outside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!pop.current?.contains(t) && !chip.current?.contains(t)) close(false);
    };
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, [open]);

  useEffect(() => { setAt(typed.trim() && shown.length > 0 ? 1 : 0); }, [typed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) document.getElementById(`${id}-o${at}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [at, open, id]);

  const keys = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setAt((i) => Math.min(rows.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAt((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); const o = rows[at]; if (o) choose(o); }
    else if (e.key === 'Escape') { e.preventDefault(); close(true); }
    else if (e.key === 'Tab') close(false);
  };

  return (
    <>
      <ActionButton reading ref={chip} type="button" className={value ? 'tg on pick' : 'tg pick'} aria-haspopup="listbox" aria-expanded={open}
        aria-label={value ? `${label}: ${chosen?.name ?? 'chosen'}` : label}
        onClick={() => (open ? close(false) : setOpen(true))}>
        <span className="pick-v">{value ? chosen?.name ?? label : label}</span>
      </ActionButton>
      {open ? (
        <div ref={pop} className="pick-pop" style={box ? { top: box.top, left: box.left, width: box.width } : { visibility: 'hidden' }}>
          <ActionInput reading ref={input} type="text" role="combobox" aria-label={find} placeholder={find} value={typed} autoComplete="off" spellCheck={false}
            aria-expanded="true" aria-controls={`${id}-l`} aria-autocomplete="list" aria-activedescendant={`${id}-o${at}`}
            onChange={(e) => setTyped(e.target.value)} onKeyDown={keys} />
          <ul id={`${id}-l`} role="listbox" aria-label={label}>
            {rows.map((o, i) => (
              <li key={o.id || 'any'} id={`${id}-o${i}`} role="option" aria-selected={o.id === value}
                className={[i === at ? 'at' : '', o.id === value ? 'on' : '', i === 0 ? 'any' : ''].filter(Boolean).join(' ') || undefined}
                onMouseEnter={() => setAt(i)} onMouseDown={(e) => e.preventDefault()} onClick={() => choose(o)}>
                <span>{o.name}</span>{o.n !== undefined ? <em>{o.n.toLocaleString('en-GB')}</em> : null}
              </li>
            ))}
          </ul>
          {options === null ? <p className="pick-note" role="status">Reading them…</p>
            : shown.length === 0 && typed.trim() ? <p className="pick-note" role="status">Nothing by that name.</p>
            : more > 0 ? <p className="pick-note">{more.toLocaleString('en-GB')} more: type to find one.</p>
            : null}
        </div>
      ) : null}
    </>
  );
}
